/**
 * Singleton API client. Thin facade over `lib/engine.ts` (axios) — the engine
 * owns CSRF, 401-refresh, problem+json mapping, and credential handling.
 *
 * Existing callers (hand-written wrappers in `lib/api/<module>.ts` and the
 * orval-generated client mutator) use the surface here; new code should
 * prefer `engine.get/.post/...` directly.
 */
import { engine, getCsrfToken, setCsrfToken } from "../engine.js";

export { getCsrfToken, setCsrfToken };

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: Method;
  url: string;
  params?: Record<string, string | number | boolean | undefined | null>;
  data?: unknown;
  headers?: Record<string, string>;
  /** For multipart uploads: FormData body; takes priority over data. */
  body?: BodyInit;
  signal?: AbortSignal;
}

/** Drop null/undefined params before handing to axios. */
function pruneParams(
  params?: RequestOptions["params"],
): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    out[k] = v;
  }
  return out;
}

/** Generic typed request used by lib/api/<module>.ts and the orval mutator. */
export async function apiRequest<T = unknown>(opts: RequestOptions): Promise<T> {
  const method = opts.method ?? "GET";

  // FormData/Blob bodies bypass `data` so axios doesn't try to JSON-stringify
  // them. `body` (BodyInit) takes priority over `data` per the legacy contract.
  const data: unknown = opts.body !== undefined ? opts.body : opts.data;

  const res = await engine.request<T>({
    method,
    url: opts.url,
    params: pruneParams(opts.params),
    data,
    headers: opts.headers,
    signal: opts.signal,
  });

  // 204 No Content → undefined, matching the prior fetch-based contract.
  if (res.status === 204) return undefined as T;
  return res.data;
}

/**
 * Orval custom mutator wrapper. Orval's `fetch` client calls this with
 * `(url, requestInit)` and the generic parameter is the FULL response
 * envelope `{ data, status, headers }`. We delegate to the engine so generated
 * endpoints flow through the same code path as hand-written wrappers (CSRF +
 * APIError + problem+json).
 */
export async function orvalMutator<
  T extends { data: unknown; status: number; headers?: Headers },
>(url: string, init: RequestInit): Promise<T> {
  const method = (init.method?.toUpperCase() as Method | undefined) ?? "GET";

  // Translate RequestInit → AxiosRequestConfig. Orval's fetch mutator already
  // produces a Headers/string/FormData body shape compatible with axios.
  const headers: Record<string, string> = {};
  if (init.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((v, k) => {
        headers[k] = v;
      });
    } else if (Array.isArray(init.headers)) {
      for (const [k, v] of init.headers) headers[k] = v;
    } else {
      Object.assign(headers, init.headers as Record<string, string>);
    }
  }

  const res = await engine.request<unknown>({
    method,
    url,
    data: init.body ?? undefined,
    headers,
    signal: (init.signal ?? undefined) as AbortSignal | undefined,
  });

  let data: unknown = res.data;
  if (res.status === 204) data = undefined;
  return {
    data,
    status: res.status,
    // Orval expects a Headers object; rebuild from axios's plain record.
    headers: new Headers(
      Object.entries(res.headers as Record<string, string>).filter(
        ([, v]) => typeof v === "string",
      ),
    ),
  } as T;
}

export default orvalMutator;
